import { useState, useEffect, useCallback, useRef } from 'react';
import { UserProfile, UserService } from '@/services/userService';
import { useAuth } from '@/contexts/useAuth';
import { toast } from 'sonner';
import { devLog } from '@/utils/devLogger';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useRoleAccess', 'rbac');

export const useRoleAccess = () => {
  const {
    user: currentAuthUser,
    session,
    refreshProfile
  } = useAuth();

  // User impersonation state
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [impersonatedUser, setImpersonatedUser] = useState<UserProfile | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const availableUsersRequestSequence = useRef(0);
  const availableUsersAbortRef = useRef<AbortController | null>(null);

  // Permission caching state
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [loadedPermissionsUserId, setLoadedPermissionsUserId] = useState<string | null>(null);
  const permissionRequestSequence = useRef(0);
  const permissionAbortRef = useRef<AbortController | null>(null);

  const hasAuthenticatedSession = Boolean(
    currentAuthUser
    && session?.access_token
    && session.user?.id === currentAuthUser.id
  );

  // Check if current user is admin (based on roles from database)
  const isAdmin = hasAuthenticatedSession && (
    currentAuthUser?.roles?.some((role: string) =>
      role === 'admin' || role === 'manager'
    ) || false
  );

  const loadAvailableUsers = useCallback(async () => {
    if (!hasAuthenticatedSession) {
      return;
    }

    availableUsersAbortRef.current?.abort();
    const controller = new AbortController();
    availableUsersAbortRef.current = controller;
    const requestSequence = availableUsersRequestSequence.current + 1;
    availableUsersRequestSequence.current = requestSequence;

    setIsLoadingUsers(true);
    try {
      devLog.users('Loading available users for admin impersonation...');
      const users = await UserService.getAllUsers({
        signal: controller.signal
      });
      if (
        controller.signal.aborted
        || availableUsersRequestSequence.current !== requestSequence
      ) {
        return;
      }

      devLog.users('Loaded users:', users);
      setAvailableUsers(users);

      // Check if we had a saved impersonated user (optional persistence)
      const savedUserId = sessionStorage.getItem('impersonated_user_id');
      if (savedUserId && savedUserId !== currentAuthUser?.id) {
        const savedUser = users.find(u => u.id === savedUserId);
        if (savedUser) {
          devLog.impersonation('Restoring impersonation for:', savedUser);
          setImpersonatedUser(savedUser);
        } else {
          sessionStorage.removeItem('impersonated_user_id');
        }
      }
    } catch (error) {
      if (
        controller.signal.aborted
        || availableUsersRequestSequence.current !== requestSequence
      ) {
        return;
      }
      devLog.error('Error loading users:', error);
      toast.error('Failed to load users for impersonation');
    } finally {
      if (availableUsersRequestSequence.current === requestSequence) {
        setIsLoadingUsers(false);
      }
      if (availableUsersAbortRef.current === controller) {
        availableUsersAbortRef.current = null;
      }
    }
  }, [currentAuthUser?.id, hasAuthenticatedSession]);

  // Load available users when user is admin
  useEffect(() => {
    if (isAdmin && hasAuthenticatedSession) {
      void loadAvailableUsers();
    } else {
      availableUsersAbortRef.current?.abort();
      availableUsersAbortRef.current = null;
      availableUsersRequestSequence.current += 1;
      setAvailableUsers([]);
      setIsLoadingUsers(false);
      setImpersonatedUser(null);
      sessionStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('dev_impersonated_user');
      localStorage.removeItem('dev_user_role');
    }

    return () => {
      availableUsersAbortRef.current?.abort();
      availableUsersAbortRef.current = null;
      availableUsersRequestSequence.current += 1;
    };
  }, [hasAuthenticatedSession, isAdmin, loadAvailableUsers]);

  const switchToUser = async (user: UserProfile | null) => {
    if (!user) {
      // Stop impersonating - clear BOTH storage mechanisms
      setImpersonatedUser(null);
      sessionStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('dev_impersonated_user'); // Fix: Clear localStorage too
      localStorage.removeItem('dev_user_role');          // Fix: Clear role override
      toast.success('Stopped impersonating user');
      await refreshProfile();
      return;
    }

    // Prevent impersonating the same user
    if (impersonatedUser && impersonatedUser.id === user.id) {
      toast.info('Already impersonating this user');
      return;
    }

    // Prevent self-impersonation
    if (currentAuthUser && currentAuthUser.id === user.id) {
      toast.warning('Cannot impersonate yourself. Use "Yourself" option instead.');
      return;
    }

    try {
      devLog.impersonation('Switching to user:', user);
      setImpersonatedUser(user);
      sessionStorage.setItem('impersonated_user_id', user.id);
      toast.success(`Now viewing as ${user.full_name || user.email}`);
    } catch (error) {
      devLog.error('Error switching user:', error);
      toast.error('Failed to switch user');
    }
  };

  const getCurrentUser = useCallback((): UserProfile | null => {
    if (!impersonatedUser) {
      // Return the actual authenticated user with extended database fields
      if (currentAuthUser && hasAuthenticatedSession) {
        return {
          id: currentAuthUser.id,
          email: currentAuthUser.email || '',
          roles: currentAuthUser.roles || [],
          first_name: currentAuthUser.firstName || '',
          last_name: currentAuthUser.lastName || '',
          full_name: currentAuthUser.fullName || currentAuthUser.email || ''
        };
      }
      return null;
    }

    // Return the impersonated user
    return impersonatedUser;
  }, [impersonatedUser, currentAuthUser, hasAuthenticatedSession]);

  // Load user permissions from database
  const loadUserPermissions = useCallback(async (userId: string) => {
    if (!hasAuthenticatedSession) {
      return;
    }

    permissionAbortRef.current?.abort();
    const controller = new AbortController();
    permissionAbortRef.current = controller;
    const requestSequence = permissionRequestSequence.current + 1;
    permissionRequestSequence.current = requestSequence;
    setIsLoadingPermissions(true);
    try {
      log.debug('Loading permissions for user', { userId });

      // Call the get_user_permissions function
      const { data, error } = await supabase
        .rpc('get_user_permissions', { user_id: userId })
        .abortSignal(controller.signal);

      if (
        controller.signal.aborted
        || permissionRequestSequence.current !== requestSequence
      ) {
        return;
      }

      if (error) {
        log.error('Error loading user permissions', { userId }, error);
        if (permissionRequestSequence.current === requestSequence) {
          // Fall back to empty permissions on error
          setUserPermissions([]);
          setLoadedPermissionsUserId(userId);
        }
        return;
      }

      const permissions = data?.map((row: { permission_id: string }) => row.permission_id) || [];
      log.info('Loaded user permissions', { userId, permissionCount: permissions.length, permissions });
      if (permissionRequestSequence.current === requestSequence) {
        setUserPermissions(permissions);
        setLoadedPermissionsUserId(userId);
      }
    } catch (error) {
      if (
        controller.signal.aborted
        || permissionRequestSequence.current !== requestSequence
      ) {
        return;
      }
      log.error('Exception loading permissions', { userId }, error as Error);
      if (permissionRequestSequence.current === requestSequence) {
        setUserPermissions([]);
        setLoadedPermissionsUserId(userId);
      }
    } finally {
      if (permissionRequestSequence.current === requestSequence) {
        setIsLoadingPermissions(false);
      }
      if (permissionAbortRef.current === controller) {
        permissionAbortRef.current = null;
      }
    }
  }, [hasAuthenticatedSession]);

  // Load permissions when user changes
  useEffect(() => {
    const userId = impersonatedUser?.id || currentAuthUser?.id;
    if (userId && hasAuthenticatedSession) {
      void loadUserPermissions(userId);
    } else {
      permissionAbortRef.current?.abort();
      permissionAbortRef.current = null;
      permissionRequestSequence.current += 1;
      setUserPermissions([]);
      setLoadedPermissionsUserId(null);
      setIsLoadingPermissions(false);
    }

    return () => {
      permissionAbortRef.current?.abort();
      permissionAbortRef.current = null;
      permissionRequestSequence.current += 1;
    };
  }, [
    currentAuthUser?.id,
    hasAuthenticatedSession,
    impersonatedUser?.id,
    loadUserPermissions
  ]);

  const permissionSubjectUserId = impersonatedUser?.id || currentAuthUser?.id || null;
  const isLoadingCurrentUserPermissions = isLoadingPermissions
    || (
      permissionSubjectUserId !== null
      && loadedPermissionsUserId !== permissionSubjectUserId
    );

  // Get role info for display (backward compatibility)
  const getCurrentRoleInfo = () => {
    const user = getCurrentUser();
    if (!user || !user.roles || user.roles.length === 0) {
      return {
        name: 'User',
        color: 'bg-gray-500'
      };
    }

    // Convert role slug to display name (e.g., 'loan_officer' -> 'Loan Officer')
    const role = user.roles[0];
    const displayName = role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Map role to color (same as user-roles.tsx)
    const roleColorMap: Record<string, string> = {
      admin: 'bg-red-500',
      manager: 'bg-blue-500',
      loan_officer: 'bg-green-500',
      underwriter: 'bg-orange-500',
      processor: 'bg-purple-500',
      compliance_specialist: 'bg-indigo-500',
      closer: 'bg-teal-500',
      loan_officer_assistant: 'bg-yellow-500',
      partner: 'bg-gray-500',
      recruiter: 'bg-pink-500'
    };

    return {
      name: displayName,
      color: roleColorMap[role] || 'bg-gray-500'
    };
  };

  // Permission check based on database permissions (with caching)
  const hasAnyPermission = (permissions: string[]): boolean => {
    const user = getCurrentUser();
    if (!user || !user.roles || user.roles.length === 0) {
      return false;
    }

    // If no permissions required, allow access
    if (!permissions || permissions.length === 0) {
      return true;
    }

    // Admin has all permissions. Manager remains a general UI superuser, but
    // Pick Up viewing is intentionally assignment-driven so team activation
    // follows the Role Permissions matrix exactly.
    const requiresExplicitPickupView = permissions.includes('view_pickup');
    if (
      user.roles.includes('admin')
      || (user.roles.includes('manager') && !requiresExplicitPickupView)
    ) {
      return true;
    }

    // If permissions are still loading, fall back to false (can be optimistic if needed)
    if (isLoadingCurrentUserPermissions) {
      return false;
    }

    // Feature-based permission check (no hierarchy needed)
    // Each permission maps 1:1 to a UI feature
    return permissions.some(p => userPermissions.includes(p));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) {
      return true;
    }
    return permissions.every(p => hasAnyPermission([p]));
  };

  // Manual permission refresh for admin testing
  const refreshPermissions = useCallback(async () => {
    const userId = impersonatedUser?.id || currentAuthUser?.id;
    if (!userId || !hasAuthenticatedSession) {
      log.warn('Cannot refresh permissions - no user available');
      return;
    }

    log.info('Manually refreshing permissions', { userId, isImpersonating: !!impersonatedUser });
    await loadUserPermissions(userId);
    toast.success('Permissions refreshed');
  }, [
    currentAuthUser?.id,
    hasAuthenticatedSession,
    impersonatedUser,
    loadUserPermissions
  ]);

  return {
    // Backward compatibility with old interface
    currentRole: getCurrentRoleInfo(),
    hasAnyPermission,
    hasAllPermissions,

    // Admin check
    isAdmin,

    // Permission data
    userPermissions,
    isLoadingPermissions: isLoadingCurrentUserPermissions,
    refreshPermissions,  // NEW: Manual refresh for testing

    // User switching
    availableUsers,
    impersonatedUser,
    isLoadingUsers,
    switchToUser,
    getCurrentUser,
    refreshUsers: loadAvailableUsers,
    isImpersonating: !!impersonatedUser
  };
};
